import React from 'react';
import cn from 'classnames';
import Occasion from '@repo/utils/helpers/Occasion';
import forgetFollowup from './modifiers/forgetFollowup';
import { Overlay, Tooltip } from '@repo/components/tooltips';
import { ReminderDetailIcon } from '@repo/icons/Reminder';
import { FollowupPluginStore } from './types';
import styles from './Followup.module.scss';
import editorStyles from '../TextEditor.module.scss';

const EmptyWrapper = ({ children }) => <>{children}</>;
const TooltipEntityWrapper = ({ children }) => (
  <FollowupTooltip placement="right">{children}</FollowupTooltip>
);
const TooltipIconWrapper = ({ children }) => (
  <FollowupTooltip placement="bottom">{children}</FollowupTooltip>
);

function FollowupTooltip({
  children,
  placement,
}: {
  children: React.ReactElement;
  placement: 'right' | 'bottom';
}) {
  const overlay = <Overlay>Delete Reminder</Overlay>;
  return (
    <Tooltip placement={placement} overlay={overlay}>
      <span style={{ display: 'inline-flex' }}>{children}</span>
    </Tooltip>
  );
}

export const FollowupIcon = ({
  followupOccasion,
  precedeWithBullet,
  onClick,
  large = false,
  entityInteracted = false,
}: {
  followupOccasion: Occasion | null;
  precedeWithBullet: boolean;
  onClick?: () => void;
  large?: boolean;
  entityInteracted?: boolean;
}) => {
  const Wrapper = onClick ? TooltipIconWrapper : EmptyWrapper;

  return followupOccasion ? (
    <>
      {precedeWithBullet && <span className={styles.bullet}>·</span>}
      <Wrapper>
        <ReminderDetailIcon
          date={followupOccasion}
          large={large}
          entityInteracted={entityInteracted}
          onClick={onClick}
          alwaysRender
        />
      </Wrapper>
    </>
  ) : null;
};

export default function followupComponent(
  store: FollowupPluginStore,
  setFollowupEntityHovered: (isHovered: boolean) => void,
  readOnly: boolean,
): React.FunctionComponent {
  const handleFollowupClick = !readOnly
    ? () => {
        if (store.initialized()) {
          const { getEditorState, setEditorState } = store.editorFns();
          const editorState = getEditorState();
          store.setFollowup(null);
          setEditorState(forgetFollowup(editorState));
        }
      }
    : undefined;

  return ({ children }) => {
    const followup = store.followupOccasion();
    const pluginInitialized = store.initialized();
    const Wrapper =
      pluginInitialized && followup && !readOnly ? TooltipEntityWrapper : EmptyWrapper;
    let timeBasedStyle = null;

    if (followup && followup.isUpcoming(5)) {
      timeBasedStyle = 'highlighted';
    }

    if (followup && followup.isPast) {
      timeBasedStyle = 'grayed';
    }

    const handleMouseEnter =
      setFollowupEntityHovered && !readOnly ? () => setFollowupEntityHovered(true) : undefined;
    const handleMouseLeave =
      setFollowupEntityHovered && !readOnly ? () => setFollowupEntityHovered(false) : undefined;

    return (
      <Wrapper>
        <span
          className={cn(
            editorStyles.customEntity,
            timeBasedStyle && editorStyles[timeBasedStyle],
            !pluginInitialized && editorStyles.noHover,
          )}
          onMouseDown={handleFollowupClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          spellCheck={false}
        >
          {children}
        </span>
      </Wrapper>
    );
  };
}
